import { useCallback, useState } from 'react'

import { startOfTodayJst } from '~/models/dateTime'

export type UseDayNavigationReturn = {
  displayDate: Date
  setDisplayDate: (date: Date) => void
  goToToday: () => void
}

/**
 * 表示日 state と本日リセット。
 *
 * 前日 / 翌日の暦日計算は共通 `~/components/common/filters/DateStepper` が内部に
 * 持つため、本 hook は持たない（画面側は `setDisplayDate` を渡すだけでよい）。
 *
 * `displayDate` は**カレンダー日 `Date`** として保持する。絶対時刻 `serverNow()` をそのまま初期値/リセット値にすると、
 * 端末 TZ が JST より進んでいる場合に JST 当日の終盤で `displayDate` が JST
 * 翌日側へズレ、`DateStepper` の maxDate 比較（暦日）と不整合を起こす
 * (issue #6361 レビュー指摘)。
 *
 * 元は `BalanceDemand` / `BalanceGeneration` の各 `useFilterState` が
 * byte-identical に複製していたロジックを抽出した共通部品。型パラメータは
 * 持たない（対象が具体的な `Date` state のみのため）。フィルタ全体の状態管理
 * （系列表示切替・エリア選択等）はページごとに完全に異なる形をしているため
 * 統合の対象外（各ページの `useFilterState` が本 hook を内部で合成し、
 * ページ固有の残り部分は個別に持つ）。
 *
 * `BalanceGenerationDetail` も同じ displayDate state + 本日リセットの形なので
 * 本 hook を直接呼ぶ（issue #6361 レビュー指摘: 時間軸の修正が複数画面と本 hook へ
 * lockstep で波及していたため統合）。`BalanceDemandDetail` は表示日を URL クエリで
 * 持つので本 hook を使わない。
 */
export function useDayNavigation(
  initial: Date = startOfTodayJst(),
): UseDayNavigationReturn {
  const [displayDate, setDisplayDate] = useState<Date>(initial)

  const goToToday = useCallback((): void => {
    setDisplayDate(startOfTodayJst())
  }, [])

  return { displayDate, setDisplayDate, goToToday }
}
